import { getPlatformModule, getPlatformModuleIds, listPlatformModules } from '../registry/modules.js';

export const TENANT_COMPOSITION_VERSION = '2026-08-core-saas-modules-tenant-v1';

export function normalizeTenantEntitlements(raw = {}) {
  if (Array.isArray(raw)) {
    return Object.fromEntries(raw.map((moduleId) => [moduleId, { enabled: true }]));
  }
  return raw && typeof raw === 'object' ? raw : {};
}

function resolveDependencyClosure(enabledIds) {
  const resolved = new Set(enabledIds);
  let changed = true;
  while (changed) {
    changed = false;
    for (const moduleId of [...resolved]) {
      const module = getPlatformModule(moduleId);
      for (const dependency of module?.dependencies || []) {
        if (!resolved.has(dependency)) {
          resolved.add(dependency);
          changed = true;
        }
      }
    }
  }
  return resolved;
}

export function composeTenantModules(tenantConfig = {}) {
  const entitlements = normalizeTenantEntitlements(tenantConfig.moduleEntitlements || tenantConfig.modules || {});
  const platformIds = new Set(getPlatformModuleIds());
  const requestedEnabled = new Set(
    Object.entries(entitlements)
      .filter(([, cfg]) => cfg?.enabled !== false)
      .map(([moduleId]) => moduleId)
      .filter((moduleId) => platformIds.has(moduleId))
  );
  const enabled = resolveDependencyClosure(requestedEnabled);

  return Object.fromEntries(
    listPlatformModules().map((module) => {
      const override = entitlements[module.id] || {};
      return [module.id, {
        enabled: enabled.has(module.id),
        label: override.label || module.name,
        source: 'qoriax-saas',
        entitlement: enabled.has(module.id) ? 'granted' : 'not_granted',
        internal: override.internal ?? module.internal ?? false,
        adapter: override.adapter || null
      }];
    })
  );
}

export function composeTenantConfig(tenantConfig = {}) {
  if (!tenantConfig?.id) return null;
  return Object.freeze({
    ...tenantConfig,
    architecture: 'core>saas>modules>tenant',
    moduleEntitlements: normalizeTenantEntitlements(tenantConfig.moduleEntitlements || tenantConfig.modules || {}),
    modules: composeTenantModules(tenantConfig)
  });
}

export function assertTenantComposition(tenantConfig = {}) {
  const errors = [];
  const entitlements = normalizeTenantEntitlements(tenantConfig.moduleEntitlements || tenantConfig.modules || {});
  const platformIds = new Set(getPlatformModuleIds());

  for (const moduleId of Object.keys(entitlements)) {
    if (!platformIds.has(moduleId)) errors.push(`tenant:${tenantConfig.id || 'unknown'} references non-SaaS module ${moduleId}`);
  }

  const composed = composeTenantConfig(tenantConfig);
  if (!composed) errors.push('tenant id is required');

  return { ok: errors.length === 0, errors, composed };
}
